"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BranchingQueryDefinition = exports.isDehyratedBranchingQueryDefinitionArray = exports.isDehyratedBranchingQueryDefinition = void 0;
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const uuid_1 = require("uuid");
const lambda_1 = require("../../lambda");
const debug = lambda_1.DEBUG_OVERRIDE || (lambda_1.NOT_LAMBDA && false);
const { REACT_APP_DEPLOYMENT_STAGE, stage, NODE_ENV } = process.env;
const throwOnDuplicateNodeIds = ['dev', 'test'].includes(stage) || ['dev', 'test'].includes(REACT_APP_DEPLOYMENT_STAGE) || NODE_ENV === 'development';
const isBranchingQueryDefinitionAttributes = (x) => {
    const y = x;
    return (typeof y.id === 'string' && y.subQueries?.every((subQuery) => isBranchingQueryDefinitionAttributes(subQuery) && y.path?.every((path) => (0, iotv_v3_types_1.isEdgeParams)(path))));
};
const isDehyratedBranchingQueryDefinition = (x) => {
    const y = x;
    return typeof y.id === 'string' && y.subQueryIds?.every((subQueryId) => typeof subQueryId === 'string' && y.path?.every((path) => (0, iotv_v3_types_1.isEdgeParams)(path)));
};
exports.isDehyratedBranchingQueryDefinition = isDehyratedBranchingQueryDefinition;
const isDehyratedBranchingQueryDefinitionArray = (x) => {
    let isValid = false;
    if (x instanceof Array) {
        isValid = x.every((queryDef) => (0, exports.isDehyratedBranchingQueryDefinition)(queryDef));
    }
    return isValid;
};
exports.isDehyratedBranchingQueryDefinitionArray = isDehyratedBranchingQueryDefinitionArray;
function getZombieIndex(id) {
    return this.findIndex((other) => other.id === id);
}
function pluckApexQuery() {
    return this.find((other) => other.isApexQuery === true);
}
/* returns an object representing a query that can be evaluated recurively providing a concise defintion to parse a query path through data */
class BranchingQueryDefinition {
    constructor({ path, subQueries = [], id }) {
        this.addSubQuery = (subQuery) => {
            this.subQueries.push(subQuery);
        };
        this.addSubQueries = (subQueries) => {
            this.subQueries.push(...subQueries);
        };
        this.dehydrate = (isApexQuery = true, dehydratedIds = []) => {
            const thisDehydrated = {
                path: this.path,
                id: this.id,
                subQueryIds: this.subQueries.map((subQuery) => subQuery.id),
            };
            if (isApexQuery) {
                thisDehydrated.isApexQuery = true;
            }
            if (throwOnDuplicateNodeIds && dehydratedIds.includes(this.id)) {
                throw new Error(`query part ${this.id} is already named in dehydratedNodeIds: ${dehydratedIds.join(', ')}`);
            }
            dehydratedIds.push(this.id);
            const dehydrated = this.subQueries.reduce((a, c) => {
                if (!dehydratedIds.includes(c.id)) {
                    a.push(...c.dehydrate(false, dehydratedIds));
                }
                return a;
            }, [thisDehydrated]);
            return dehydrated;
        };
        this.hydrateFromList = (zombieQueryDefinitions, queryMap) => {
            const parentId = this.id;
            queryMap[parentId] = this;
            const thisEntryIdx = getZombieIndex.apply(zombieQueryDefinitions, [this.id]);
            const thisEntry = zombieQueryDefinitions[thisEntryIdx];
            const removedEntry = zombieQueryDefinitions.splice(thisEntryIdx, 1);
            if (thisEntry) {
                const subQueriesIds = thisEntry.subQueryIds;
                for (let subQueryId of subQueriesIds) {
                    let resolvedSubQuery;
                    /** in the case of recursive structures get the previously processed node from the map */
                    if (queryMap[subQueryId]) {
                        resolvedSubQuery = queryMap[subQueryId];
                        this.addSubQuery(resolvedSubQuery);
                        /** where not previsouly processed get new query and add to map */
                    }
                    else {
                        const idx = getZombieIndex.apply(zombieQueryDefinitions, [subQueryId]);
                        resolvedSubQuery = new BranchingQueryDefinition({ ...zombieQueryDefinitions[idx], subQueries: [] });
                        queryMap[resolvedSubQuery.id] = resolvedSubQuery;
                        this.addSubQuery(resolvedSubQuery);
                        resolvedSubQuery.hydrateFromList(zombieQueryDefinitions, queryMap);
                    }
                }
            }
        };
        /** returns matched subquery if exists. The purpose of this is to be able to manipulate automatically generated query definitions, where the generator uses a predicatble id for each sub query
         * @param visitedIds is a recursion limiting array which does not need to be provided by caller
         */
        this.getSubQueryById = ({ id, visitedIds = [] }) => {
            let res = null;
            visitedIds.push(this.id);
            if (this.id === id) {
                res = this;
            }
            else {
                for (let subQuery of this.subQueries) {
                    if (!visitedIds.includes(subQuery.id)) {
                        const getSubQueryByIdFromSubQueryRes = subQuery.getSubQueryById({ id, visitedIds });
                        if (getSubQueryByIdFromSubQueryRes) {
                            res = getSubQueryByIdFromSubQueryRes;
                            break;
                        }
                    }
                }
            }
            return res;
        };
        /** adds subQuery to matched nodeId.  The purpose of this is to be able to manipulate automatically generated query definitions, where the generator uses a predicatble id for each sub query
         * The exact id may not be predictable where the query definition is manufactured by a function which assigns id prefixes to avoid duplicate ids in composite queries such as
         * getNestedStructureBranchingQuery
         *
         */
        this.addSubQueryById = (nodeId, additionalQuery) => {
            const matchedQuery = this.getSubQueryById({ id: nodeId });
            if (matchedQuery) {
                matchedQuery.addSubQuery(additionalQuery);
                debug && console.log(`attached ${additionalQuery.id} extension to ${nodeId}`);
            }
            else {
                console.log(`Did not attache ${additionalQuery.id} extension to ${nodeId}!!`);
            }
        };
        this.summarize = (visitedIds = []) => {
            return {
                id: this.id,
                path: this.path,
                subQueries: this.subQueries
                    .filter((subQuery) => !visitedIds.includes(subQuery.id))
                    .map((subQuery) => {
                    visitedIds.push(subQuery.id);
                    return subQuery.summarize(visitedIds);
                }),
            };
        };
        this.path = path;
        this.subQueries = subQueries;
        this.id = id ?? (0, uuid_1.v4)();
    }
}
exports.BranchingQueryDefinition = BranchingQueryDefinition;
BranchingQueryDefinition.rehydrate = (zombieQueryDefinitions) => {
    const queryMap = {};
    let apexQuery = undefined;
    const apexQueryZombie = pluckApexQuery.apply(zombieQueryDefinitions);
    if (apexQueryZombie) {
        apexQuery = new BranchingQueryDefinition({ ...apexQueryZombie, subQueries: [] });
        apexQuery.hydrateFromList(zombieQueryDefinitions, queryMap);
    }
    return apexQuery;
};
